'use client'

import { useEffect, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { Check, Database, Download, FileCheck2, Link2, RefreshCw, ShieldCheck, UserRound } from 'lucide-react'
import { DemoFrame } from './demoKit'
import { useOnScreen, useSequence } from './demoHooks'

// GHL API & custom development: the four things the page says GHL Prime
// builds outside the native GoHighLevel screens, each as a small working
// example (one insurance-agency story throughout). Cycles through the tabs on
// its own until the visitor picks one; Approvals and Marketplace app can be
// clicked through.

const TABS = [
  { key: 'object', label: 'Custom object' },
  { key: 'approval', label: 'Approvals' },
  { key: 'app', label: 'Marketplace app' },
  { key: 'sync', label: 'Background sync' },
]

const FIELDS = [
  ['Policy number', 'PL-20481'],
  ['Coverage', '$250,000'],
  ['Effective date', 'Nov 1, 2026'],
  ['Renewal', 'Nov 1, 2027'],
]
const FIELD_DELAYS = [300, 450, 450, 450, 600]

function CustomObject({ run }) {
  const step = useSequence(FIELD_DELAYS, { run })
  return (
    <div className="svd-api-pane">
      <div className="svd-record">
        <div className="svd-record-head">
          <span className="svd-record-icon" aria-hidden="true"><Database size={17} /></span>
          <span>
            <small>Custom object</small>
            <strong>Insurance Policy</strong>
          </span>
          <span className={`svd-chip${step >= 5 ? ' is-good' : ''}`}>{step >= 5 ? 'Active' : 'Draft'}</span>
        </div>
        <dl>
          {FIELDS.map(([name, value], i) => (
            <div key={name}>
              <dt>{name}</dt>
              <dd>{step > i ? <motion.span initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }}>{value}</motion.span> : <span className="svd-skeleton" />}</dd>
            </div>
          ))}
        </dl>
        <div className="svd-record-links">
          <span className="svd-link-chip"><UserRound size={13} aria-hidden="true" /> Jane Cooper</span>
          <span className="svd-link-chip"><Link2 size={13} aria-hidden="true" /> Home + auto bundle</span>
        </div>
      </div>
      <p className="svd-api-note">Business data the standard contact and opportunity records can&apos;t hold, linked to both.</p>
    </div>
  )
}

const STAGES = ['Underwriter', 'Manager', 'Issued']

function Approvals({ run, onUse }) {
  const [stage, setStage] = useState(0)
  // Moves itself along while untouched, so the panel never sits still.
  const [manual, setManual] = useState(false)
  useEffect(() => {
    if (!run || manual || stage >= STAGES.length) return undefined
    const timer = setTimeout(() => setStage((s) => s + 1), 1500)
    return () => clearTimeout(timer)
  }, [run, manual, stage])
  const done = stage >= STAGES.length
  const press = () => {
    onUse()
    setManual(true)
    setStage((s) => (s >= STAGES.length ? 0 : s + 1))
  }
  return (
    <div className="svd-api-pane">
      <div className="svd-approvals">
        <span className="svd-approvals-title"><FileCheck2 size={16} aria-hidden="true" /> Policy PL-20481</span>
        <ol className="svd-stages">
          {STAGES.map((name, i) => (
            <li key={name} className={i < stage ? 'is-done' : i === stage ? 'is-current' : ''}>
              <span className="svd-stage-dot" aria-hidden="true">{i < stage ? <Check size={13} strokeWidth={3} /> : i + 1}</span>
              <span>{name}</span>
            </li>
          ))}
        </ol>
        <div className="svd-approvals-foot">
          <span className={`svd-chip${done ? ' is-good' : ' is-wait'}`}>
            {done ? 'Issued · client notified' : `Waiting on ${STAGES[stage].toLowerCase()}`}
          </span>
          <button type="button" className="svd-btn" onClick={press}>
            {done ? 'Start over' : `Approve as ${STAGES[stage].toLowerCase()}`}
          </button>
        </div>
      </div>
      <p className="svd-api-note">Multi-step sign-off that GoHighLevel enforces: nothing is issued until each stage approves.</p>
    </div>
  )
}

const ACCOUNTS = ['Cooper Insurance', 'Bay Area Brokers', 'Summit Cover Co.']

function MarketplaceApp({ run, onUse }) {
  const [installs, setInstalls] = useState(0)
  const [started, setStarted] = useState(false)
  useEffect(() => {
    if (!started || installs >= ACCOUNTS.length) return undefined
    const timer = setTimeout(() => setInstalls((n) => n + 1), 700)
    return () => clearTimeout(timer)
  }, [started, installs])
  // Installs itself once if the visitor doesn't.
  useEffect(() => {
    if (!run || started) return undefined
    const timer = setTimeout(() => setStarted(true), 1200)
    return () => clearTimeout(timer)
  }, [run, started])
  const press = () => {
    onUse()
    if (installs >= ACCOUNTS.length) { setInstalls(0); setStarted(false); return }
    setStarted(true)
  }
  return (
    <div className="svd-api-pane">
      <div className="svd-app">
        <div className="svd-app-head">
          <span className="svd-app-logo" aria-hidden="true">PD</span>
          <span>
            <strong>PolicyDesk</strong>
            <small>Your app on the HighLevel Marketplace</small>
          </span>
          <button type="button" className="svd-btn" onClick={press}>
            <Download size={14} aria-hidden="true" /> {installs >= ACCOUNTS.length ? 'Reset' : started ? 'Installing…' : 'Install'}
          </button>
        </div>
        <ul className="svd-installs">
          {ACCOUNTS.map((name, i) => (
            <li key={name} className={i < installs ? 'is-done' : started && i === installs ? 'is-busy' : ''}>
              <span>{name}</span>
              <span className="svd-install-bar" aria-hidden="true"><i /></span>
              <span className="svd-install-state">{i < installs ? 'Installed' : started && i === installs ? 'Installing' : 'Sub-account'}</span>
            </li>
          ))}
        </ul>
      </div>
      <p className="svd-api-note">One app, installed into every client sub-account, built and maintained by us.</p>
    </div>
  )
}

const LOG = [
  ['02:00', 'Nightly sync started'],
  ['02:01', '1,284 policies checked against the carrier'],
  ['02:01', '37 renewals updated in GoHighLevel'],
  ['02:02', 'Renewal reminders queued in workflows'],
]
const LOG_DELAYS = [400, 900, 900, 900]

function BackgroundSync({ run }) {
  const step = useSequence(LOG_DELAYS, { run })
  const checked = step >= 2 ? '1,284' : step >= 1 ? '612' : '0'
  const updated = step >= 3 ? '37' : '0'
  return (
    <div className="svd-api-pane">
      <div className="svd-sync">
        <div className="svd-sync-head">
          <RefreshCw size={16} className={step < 4 ? 'svd-spin' : ''} aria-hidden="true" />
          <strong>Policy sync service</strong>
          <span className={`svd-chip${step >= 4 ? ' is-good' : ' is-wait'}`}>{step >= 4 ? 'Done' : 'Running'}</span>
        </div>
        <div className="svd-sync-stats">
          <span><b>{checked}</b> checked</span>
          <span><b>{updated}</b> updated</span>
          <span><b>0</b> errors</span>
        </div>
        <ul className="svd-log">
          {LOG.slice(0, step).map(([time, text]) => (
            <motion.li key={text} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}>
              <time>{time}</time> {text}
            </motion.li>
          ))}
        </ul>
      </div>
      <p className="svd-api-note">Server-side jobs that run on a schedule, outside GoHighLevel, and write the results back in.</p>
    </div>
  )
}

export default function ApiExplorer() {
  const [ref, onScreen] = useOnScreen()
  const [tab, setTab] = useState('object')
  const [auto, setAuto] = useState(true)
  const [run, setRun] = useState(0)

  useEffect(() => {
    if (!auto || !onScreen) return undefined
    const timer = setTimeout(() => {
      setTab((current) => TABS[(TABS.findIndex((t) => t.key === current) + 1) % TABS.length].key)
    }, 6500)
    return () => clearTimeout(timer)
  }, [auto, onScreen, tab])

  const stopAuto = () => setAuto(false)
  const pick = (key) => {
    stopAuto()
    setTab(key)
    setRun((n) => n + 1)
  }

  const panes = {
    object: <CustomObject run={onScreen} />,
    approval: <Approvals run={onScreen} onUse={stopAuto} />,
    app: <MarketplaceApp run={onScreen} onUse={stopAuto} />,
    sync: <BackgroundSync run={onScreen} />,
  }

  return (
    <DemoFrame
      frameRef={ref}
      className="svd-api"
      title="Built beyond the native screens"
      label="Demo: custom objects, approval workflows, a marketplace app and a background sync built on the GoHighLevel API"
      tabs={TABS}
      activeTab={tab}
      onTab={pick}
      onReplay={() => pick(tab)}
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={`${tab}-${run}`}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.25 }}
        >
          {panes[tab]}
        </motion.div>
      </AnimatePresence>

      <div className="svd-split" aria-label="About 80% of a build is native GoHighLevel setup; the custom 20% is what we code">
        <div className="svd-split-bar">
          <span className="svd-split-native">Native GoHighLevel setup · 80%</span>
          <span className="svd-split-custom"><ShieldCheck size={13} aria-hidden="true" /> 20%</span>
        </div>
        <span className="svd-split-caption">The 20% that makes your business different is custom code.</span>
      </div>
    </DemoFrame>
  )
}
